import { apiClient } from '@/core/infra_api_client'
import { staffRolloutPath } from './rollout_target'

/**
 * The settings card (PLAN-20261013 slice 08d, agion/Agion#3155): per
 * organisation, the switch that lets its owners and admins write catalogued
 * values, and the staff range of each live setting. Only the staff route
 * serves it, for the active organisation as for any other: GET/PUT
 * /v1/infra/staff/rollout/settings/organizations/{id}, forwarded by
 * infra-service to governance behind the verified platform administrator
 * check. The server validates every range and decides every refusal.
 */
export type SettingsRolloutLevel = 'off' | 'on'

/** A setting value: a whole amount, or unlimited. */
export interface SettingValue {
  amount?: number
  unlimited?: boolean
}

export interface SettingRange {
  minimum: SettingValue
  maximum: SettingValue
}

/** One setting's staff range as the server shows it on the card. */
export interface SettingsCardRange {
  key: string
  name: string
  unit: string
  /** The organisation's own staff range; null when it uses the default. */
  own: SettingRange | null
  /** The range that applies without an own range (the catalogue's). */
  default: SettingRange
  /** The server takes an unlimited value in this range; the card offers it only then. */
  allowsUnlimited: boolean
}

export interface SettingsRolloutPolicy {
  organizationId: string
  level: SettingsRolloutLevel
  revision: number
  ranges: SettingsCardRange[]
}

/**
 * A history row. The range maps are keyed by setting id as the response
 * converter leaves them (snake_case parts turned to camelCase), so a reader
 * looks them up with snakeToCamel(range.key).
 */
export interface SettingsRolloutHistoryEntry {
  organizationId: string
  revision: number
  previousLevel: SettingsRolloutLevel
  level: SettingsRolloutLevel
  previousRanges?: Record<string, SettingRange>
  ranges?: Record<string, SettingRange>
  actorId: string
  reason: string
  changedAt: string
}

interface SettingsRolloutHistoryResponse {
  history: SettingsRolloutHistoryEntry[]
}

/**
 * A card change. `ranges` names only the settings whose range changes: a
 * range replaces the organisation's own, null returns it to the default.
 */
export interface ChangeSettingsRolloutPolicy {
  expectedRevision: number
  level: SettingsRolloutLevel
  ranges?: Record<string, SettingRange | null>
  reason: string
  confirmed: true
}

export async function getSettingsRolloutPolicy(organizationId: string): Promise<SettingsRolloutPolicy> {
  const policy = await apiClient.get<SettingsRolloutPolicy>(staffRolloutPath('settings', organizationId))
  return { ...policy, ranges: policy.ranges ?? [] }
}

export async function listSettingsRolloutHistory(organizationId: string): Promise<SettingsRolloutHistoryEntry[]> {
  const response = await apiClient.get<SettingsRolloutHistoryResponse>(staffRolloutPath('settings', organizationId, 'history'))
  return response.history ?? []
}

export async function changeSettingsRolloutPolicy(
  organizationId: string,
  command: ChangeSettingsRolloutPolicy,
): Promise<SettingsRolloutPolicy> {
  const policy = await apiClient.put<SettingsRolloutPolicy, ChangeSettingsRolloutPolicy>(staffRolloutPath('settings', organizationId), command)
  return { ...policy, ranges: policy.ranges ?? [] }
}
